import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { ContextMenuDividerComponent } from '@frontendlib/components/features/popover/components/context-menu-divider/context-menu-divider.component';
import { ContextMenuHeadingComponent } from '@frontendlib/components/features/popover/components/context-menu-heading/context-menu-heading.component';
import { ContextMenuItemComponent } from '@frontendlib/components/features/popover/components/context-menu-item/context-menu-item.component';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { SortOrder } from '@modules/common/types/sort-order';
import { dateRangeChanges } from '@modules/common/utils/date-range-filter.util';
import { ElementsModule } from '@modules/elements/elements.module';
import { DatePicker2Component } from '@modules/form-controls/components/date-picker-2/date-picker-2.component';
import { type StitchTypeFilters } from '@modules/knowledge/types/stitch-type-filters';
import { FormsKpListState, type FormsListSortField } from '../../types/forms-kp-list-state';

@Component({
  selector: 'app-forms-list-context-menu',
  standalone: true,
  templateUrl: './forms-list-context-menu.component.html',
  styleUrls: ['../../../sheets/components/sheets-list-context-menu/sheets-list-context-menu.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Button2Component,
    ContextMenuDividerComponent,
    ContextMenuHeadingComponent,
    ContextMenuItemComponent,
    DateFormatPipe,
    ElementsModule,
    IconComponent,
    DatePicker2Component,
  ],
})
export class FormsListContextMenuComponent implements OnInit {
  @Input({ required: true }) public state!: FormsKpListState;
  public readonly innerFromDate = new FormControl<Date | null>(null);
  public readonly innerToDate = new FormControl<Date | null>(null);
  public dateButtonSelected: 'from' | 'to' = 'from';
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetector = inject(ChangeDetectorRef);

  public ngOnInit(): void {
    const sync = (): void => {
      this.innerFromDate.setValue(this.state.from ?? null, { emitEvent: false });
      this.innerToDate.setValue(this.state.to ?? null, { emitEvent: false });
      this.changeDetector.markForCheck();
    };
    sync();
    this.state.changes().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(sync);
    dateRangeChanges(this.innerFromDate, this.innerToDate)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ from, to }) => {
        this.state.from = from;
        this.state.to = to;
        this.state.notifyStateChanged();
      });
  }

  public sort(by: FormsListSortField): void {
    this.state.sortOrder =
      this.state.sortBy === by
        ? this.state.sortOrder === SortOrder.ASC
          ? SortOrder.DESC
          : SortOrder.ASC
        : by === 'title'
          ? SortOrder.ASC
          : SortOrder.DESC;
    this.state.sortBy = by;
    this.state.notifyStateChanged();
  }
  public pinnedOnTop() {
    this.state.pinnedOnTop = !this.state.pinnedOnTop;
    this.state.notifyStateChanged();
  }
  public flaggedOnTop() {
    this.state.flaggedOnTop = !this.state.flaggedOnTop;
    this.state.notifyStateChanged();
  }
  public flag(): void {
    this.state.flagged = this.state.flagged ? undefined : true;
    this.state.notifyStateChanged();
  }
  public pin(): void {
    this.state.pinned = this.state.pinned ? undefined : true;
    this.state.notifyStateChanged();
  }
  public stitch(value: StitchTypeFilters): void {
    this.state.stitchedWith = value;
    this.state.notifyStateChanged();
  }
  public clearSelectedDate(): void {
    this.state.from = null;
    this.state.to = null;
    this.state.notifyStateChanged();
  }
}
